/**
 * ワークスペースドメインスキーマ — エンティティ + CRUD
 */
import { z } from 'zod';
import { foldDoubleQuotes } from '../utils/fold-display-name';
import { AutomationConfigSchema } from './automation-schemas';
import { AIProviderTypeSchema, ModelSelectionSchema, ReasoningLevelSchema } from './base-schemas';
import { MemoryConfigSchema } from './memory-schemas';
import { WorkspaceHooksSchema } from './workspace-hooks-schemas';
// ========================================
// システムワークスペース（アプリ組み込み・仮想提供）
// ========================================
/**
 * 「セットアップコンシェルジュ」システムワークスペースの予約 ID。
 * 自動生成 ID（`ws-{timestamp}-{random}`）と衝突しない固定文字列。
 * DB レコードは持たず、サーバー応答時にコード定義から合成される。
 */
export const SYSTEM_SETUP_SUPPORT_WORKSPACE_ID = 'ws-system-setup-support';
/**
 * 「スキルコンシェルジュ」システムワークスペースの予約 ID。
 * スキル編集画面の右ペインからスキル作成・改善を支援する第2のシステム WS。
 * セットアップコンシェルジュと同様に DB レコードを持たず応答時に合成される。
 */
export const SYSTEM_SKILL_CONCIERGE_WORKSPACE_ID = 'ws-system-skill-concierge';
/**
 * システムワークスペースの予約 ID 集合（順序は一覧合成の順序に対応）。
 * 第3のシステム WS 追加時はこの集合へ定数を足すだけで判定・ガードが追従する。
 */
export const SYSTEM_WORKSPACE_IDS = [
    SYSTEM_SETUP_SUPPORT_WORKSPACE_ID,
    SYSTEM_SKILL_CONCIERGE_WORKSPACE_ID,
];
/** ワークスペース ID がいずれかのシステムワークスペースの予約 ID かを判定する */
export function isSystemWorkspaceId(workspaceId) {
    return SYSTEM_WORKSPACE_IDS.includes(workspaceId);
}
/**
 * ワークスペース ID がスキルコンシェルジュ WS かを判定する。
 * スキル編集画面外でのタスク開始ガード（Req 2.6）の判定に用いる識別述語。
 */
export function isSkillConciergeWorkspaceId(workspaceId) {
    return workspaceId === SYSTEM_SKILL_CONCIERGE_WORKSPACE_ID;
}
// ========================================
// コンテキスト圧縮設定
// ========================================
/**
 * コンテキスト圧縮設定スキーマ
 */
export const CompressionConfigSchema = z.object({
    enabled: z.boolean().default(true),
    triggerThresholdPercent: z.number().min(10).max(100).default(80),
    recentTurnsToKeep: z.number().min(1).max(50).default(10),
});
// ========================================
// Advisor 設定
// ========================================
/**
 * Advisor 設定スキーマ
 * null: Advisor 無効、オブジェクト: Advisor 有効
 */
export const AdvisorConfigSchema = z
    .object({
    /** Advisor モデルの AI プロバイダー */
    advisorProvider: AIProviderTypeSchema.default('bedrock'),
    /** Advisor モデル ID（プロバイダー固有の ID） */
    advisorModelName: z.string().min(1),
    /** リクエストあたりの最大呼び出し回数 */
    maxUsesPerRequest: z.number().int().positive().optional().default(3),
    /** 会話あたりの最大呼び出し回数 */
    maxUsesPerConversation: z.number().int().positive().optional().default(10),
    /** Advisor モデルの推論レベル（エフォート）。省略時: 旧形式の値、それもなければ Executor と同じ */
    advisorReasoningLevel: ReasoningLevelSchema.optional(),
    /**
     * 旧形式の Reasoning モード（互換用）。旧バージョンのアプリはこの列挙を厳密に検証するため、
     * 値の範囲は on / off から広げない。新しいアプリは advisorReasoningLevel と併せて互換値を書く
     */
    advisorReasoningMode: z.enum(['on', 'off']).optional(),
})
    .nullable();
// ========================================
// 承認バイパス設定
// ========================================
/**
 * ワークスペース単位の承認バイパス設定スキーマ
 * null: バイパス無効、オブジェクト: 設定あり（enabled で有効/無効を切替）
 */
export const ApprovalBypassConfigSchema = z
    .object({
    /** バイパスが有効かどうか */
    enabled: z.boolean(),
})
    .nullable();
// ========================================
// AI自動判定（AI Auto-Approval）設定
// ========================================
/**
 * ワークスペース単位のAI自動判定設定スキーマ。
 *
 * 承認要求のユーザープロンプト直前に LLM が安全性を評価し、安全な操作のみ自動許可する。
 * 承認バイパス（`approvalBypassConfig`）とは相互排他の承認モード（UI が択一を保証し、
 * 両方有効な不整合データは実行時に AI自動判定を優先する = 安全側）。
 *
 * null: 未設定 = 既定 = 無効（opt-in）
 */
export const AiApprovalConfigSchema = z
    .object({
    /** AI自動判定が有効かどうか */
    enabled: z.boolean(),
})
    .nullable();
// ========================================
// WebSearch 設定
// ========================================
/**
 * ワークスペース単位の WebSearch 設定スキーマ。
 *
 * 漏洩防止のため「既定で無効（opt-in）」「有効なら常に承認必須」とし、承認省略（autoApprove）は持たない。
 * 承認をスキップしたい場合は別機構のワークスペース承認バイパス（`approvalBypassConfig`）を用いる。
 *
 * - `enabled`: WebSearch ツールを有効にするか（無効なら組み立て対象に含めない）
 * - `null`: 未設定 = 既定 = `enabled:false`（無効）。
 *   ※ 利用者が明示的に有効化するまで WebSearch は使用できない（opt-in、漏洩防止優先）。
 */
export const WebSearchConfigSchema = z
    .object({
    /** WebSearch ツールを有効にするか。有効時は実行ごとに承認を要求する。 */
    enabled: z.boolean(),
})
    .nullable();
/**
 * WebSearch 設定を解決済みの状態に正規化する共有ヘルパー。
 * 未設定（`null` / `undefined`）は「無効」として解釈する（opt-in。利用者が明示的に有効化する必要がある）。
 * 消費側（実行基盤のツール組み立て・設定 UI）で再利用する。
 */
export function resolveWebSearchConfig(config) {
    if (config == null) {
        return { enabled: false };
    }
    return { enabled: config.enabled };
}
// ========================================
// メール送信設定
// ========================================
/**
 * ワークスペース単位のメール送信（Deferred Tool）設定スキーマ。
 *
 * 意図しないワークスペースでメール送信が既定で利用可能になることを防ぐため
 * `webSearchConfig` と同じ「既定で無効（opt-in）」規約に従う。
 * 送信前承認は本ツール側で常に必須（`requiresApproval: true`）とするため、
 * autoApprove のような承認省略フィールドは持たない。
 *
 * - `enabled`: メール送信ツールを有効にするか（無効なら組み立て対象に含めない）
 * - `null`: 未設定 = 既定 = `enabled:false`（無効）。
 *   ※ 利用者が明示的に有効化するまでメール送信は使用できない（opt-in）。
 *
 * ## プラグイン経由の伝播（`webSearchConfig` と同型）
 *
 * プラグインのパッケージ／公開／インストール（`plugin-packager` / `use-plugin-publish` /
 * `plugin-electron-deps`）では、公開元ワークスペースの本設定をそのまま配布物へ含め、
 * インストール先へ引き継ぐ。以前は「配布物を入れただけで送信能力が付与される」ことを避けるため
 * 意図的に伝播させない扱いだったが、以下の理由から `webSearchConfig` と同じ伝播扱いへ変更した
 * （2026-08-04、利用者からの明示的な指示による）:
 *
 * - 送信先ドメインの許可リストは実行環境（バックエンド）側が唯一の判定基準を持ち、この設定が
 *   有効化されていても、導入先の実行環境で許可ドメインが未設定なら全送信が拒否される
 *   （フェイルクローズド。設定の伝播だけでは実害を生まない）。
 * - 送信は有効化後も実行ごとに承認ダイアログが必須（`requiresApproval: true`）で、承認を
 *   スキップする経路を本ツール自身は持たない。伝播しても「無承認で送信される」ことにはならない。
 * - インストール後、利用者はワークスペース設定でいつでも無効化できる。
 *
 * 一方、セットアップコンシェルジュの設定管理ツール
 * （`manage__createDedicatedWorkspace` / `manage__updateDedicatedWorkspace`）からも
 * `webSearchConfig` と同様に設定できる。両ツールとも `requiresApproval: true` で、
 * ApprovalDialog に構成内容が提示されたうえで利用者が承認した場合のみ適用されるため、
 * 「エージェントが勝手に送信能力を付与する」経路にはならない。
 */
export const EmailSendingConfigSchema = z
    .object({
    /** メール送信ツールを有効にするか。有効時も実行ごとに承認を要求する。 */
    enabled: z.boolean(),
})
    .nullable();
/**
 * メール送信設定を解決済みの状態に正規化する共有ヘルパー。
 * 未設定（`null` / `undefined`）は「無効」として解釈する（opt-in）。
 */
export function resolveEmailSendingConfig(config) {
    if (config == null) {
        return { enabled: false };
    }
    return { enabled: config.enabled };
}
// ========================================
// 完了基準検証（停止ゲート）設定
// ========================================
/**
 * ワークスペース完了基準（floor）の入力スキーマ。
 * 自然言語の受け入れ基準＋充足確認のための根拠ヒント・no-op 条項を表現する。
 */
export const CompletionCriterionInputSchema = z.object({
    /** 受け入れ基準（自然言語） */
    description: z.string().min(1),
    /** 充足確認に使う根拠ヒント（成果物本文・ツール結果・ファイル・通知など） */
    evidenceHint: z.string().optional(),
    /** 「対応不要」を確認できた状態を完了として認める条項 */
    noOp: z.boolean().optional().default(false),
});
/**
 * 完了基準検証（停止ゲート）設定スキーマ。
 * null: 検証無効（既定・opt-in）、オブジェクト: enabled で有効/無効を切替。
 *
 * 自走タスクの停止試行時に、別軽量モデルが完了基準（floor）の充足を判定する。
 * `criteria` はワークスペースが定める必須の土台（floor）で、AI はビルトインツールで
 * 上乗せできるが floor を弱め・無効化することはできない。
 */
export const CompletionCriteriaConfigSchema = z
    .object({
    /** 完了基準検証の有効/無効 */
    enabled: z.boolean(),
    /** ワークスペース floor 基準（必須の最低ライン） */
    criteria: z.array(CompletionCriterionInputSchema),
    /** チェッカーモデル ID（省略時: 'haiku'） */
    checkerModel: z.string().optional(),
    /** 継続（unmet→再試行）の最大反復回数（既定: 5） */
    maxContinuations: z.number().int().positive().optional().default(5),
    /** 連続 stalled の許容回数（既定: 2） */
    noProgressThreshold: z.number().int().positive().optional().default(2),
})
    .nullable();
// ========================================
// セッション利用料上限設定
// ========================================
/**
 * セッション利用料上限設定スキーマ（ワークスペース単位・利用者設定）。
 * null: 上限なし（既定）、オブジェクト: limitUSD を上限として全セッションに適用。
 *
 * 管理者が定める月額利用料上限（既存）とは独立した別レイヤーとして併存する。
 * 対話チャット・スケジュール自走を問わず、当該ワークスペースの全セッションへ適用する。
 */
export const SessionCostLimitConfigSchema = z
    .object({
    /** 1セッションあたりの累積利用料上限（USD） */
    limitUSD: z.number().positive(),
})
    .nullable();
/**
 * ワークスペース間連携（相手ワークスペースのエージェントへのメッセージ）の設定。
 *
 * やり取りは送り手の許可一覧と受け手の受け入れ設定が揃った組み合わせでのみ成立する
 * （双方向 opt-in）。いずれも既定は無効であり、未設定（null）は「話しかけない・
 * 受け取らない」と解釈される。既存ワークスペースは全て null として読まれるため、
 * アップグレードによって自動的に話せるようになることはない。
 */
export const WorkspacePeerMessagingConfigSchema = z
    .object({
    /**
     * メッセージを送ってよい相手ワークスペースの ID 一覧。空配列は誰にも送らない。
     * ID の存在保証はスキーマでは行わず、実行時に解決する（削除済み ID は相手として扱わない）。
     */
    allowedPeerWorkspaceIds: z
        .array(z.string())
        // 重複は保存時に取り除く（順序は初出を保つ）
        .transform((ids) => [...new Set(ids)])
        .optional()
        .default([]),
    /** 他ワークスペースからのメッセージを受け入れるか。既定は false。 */
    acceptIncoming: z.boolean().optional().default(false),
})
    .nullable();
/**
 * Hook のタイムアウト・実行エラー時のフェイルセーフモード（ワークスペース単位）。
 * null: 既定（'fail-open' として扱う）、'fail-open': 許可優先、'fail-closed': 拒否優先。
 */
export const HookFailSafeModeSchema = z.enum(['fail-open', 'fail-closed']).nullable();
// ========================================
// サンプルプロンプト
// ========================================
/** サンプルプロンプト */
export const SamplePromptSchema = z.object({
    title: z.string().min(1),
    body: z
        .string()
        .min(1)
        .describe('{{変数名}} 形式のテンプレート変数プレースホルダーはワークスペースの templateVars で解決される（rule と同様）。'),
    autoSend: z.boolean().optional().default(false),
});
// ========================================
// 初期チャットメッセージ
// ========================================
/** 初期チャットメッセージの最大文字数 */
export const INITIAL_CHAT_MESSAGE_MAX_LENGTH = 4000;
/**
 * 初期チャットメッセージスキーマ
 * - null または非空文字列を保持
 * - 空文字・空白のみは null に正規化
 * - 最大長超過は Zod エラー
 */
export const InitialChatMessageSchema = z
    .union([z.string().max(INITIAL_CHAT_MESSAGE_MAX_LENGTH), z.null()])
    .transform((value) => {
    if (value === null)
        return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : value;
});
// ========================================
// エージェント名
// ========================================
/**
 * エージェント名の最大文字数。
 *
 * 名前としての妥当性と連携表示名一覧の可読性が主目的であり、プロンプト注入対策の
 * 主軸ではない（25 文字でも短い注入文は収まる。主防御は下記の引用符正規化と、
 * 名前が呼称のラベルであることをプロンプト側で明示すること）。
 */
export const AGENT_NAME_MAX_LENGTH = 25;
/**
 * エージェント名が未設定のときに用いる既定名。
 *
 * システムプロンプトの名前の宣言・連携表示名の導出・設定画面の案内が、
 * いずれもこの定数を参照する。
 */
export const DEFAULT_AGENT_NAME = 'きゅーもAI エージェント';
/**
 * エージェント名スキーマ
 * - null または非空文字列を保持
 * - 前後の空白を除去し、空白のみは null に正規化
 * - 二重引用符を全角引用符へ置換する。エージェント名はシステムプロンプト本体の
 *   `You are "<name>"` という宣言文へ埋め込まれ、かつプラグイン配布を通じて第三者から
 *   供給され得るため、保存される値の時点で引用構造を破れないことを保証する
 *   （`sanitizeInlineText` は改行と既知の終了タグしか除去せず、`"` は通過する）
 * - 最大長判定は trim 後の長さに対して行う（前後の空白だけで上限を超えた入力を
 *   誤って拒否しないため）。超過は切り詰めずに Zod エラー
 */
export const AgentNameSchema = z
    .string()
    .nullable()
    .transform((value, ctx) => {
    if (value === null)
        return null;
    const normalized = foldDoubleQuotes(value).trim();
    if (normalized.length > AGENT_NAME_MAX_LENGTH) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `エージェント名は ${AGENT_NAME_MAX_LENGTH} 文字以内で入力してください`,
        });
        return z.NEVER;
    }
    return normalized.length === 0 ? null : normalized;
});
// ========================================
// ワークスペースドキュメント
// ========================================
/** ワークスペースドキュメントメタデータ */
export const WorkspaceDocumentSchema = z.object({
    fileName: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
});
// ========================================
// エンティティ
// ========================================
/**
 * ワークスペース
 */
export const WorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    rule: z.string(),
    /** null: 全て有効（フィルタなし）、[]: 全て無効、string[]: ホワイトリスト */
    enabledTools: z.array(z.string()).nullable().default([]),
    /**
     * チャット画面で起動できる MCP プロンプト。
     * ID は `mcp__{serverName}__{promptName}`（enabledTools と同じ命名規則）。
     * null: 全て有効（フィルタなし）、[]: 全て無効、string[]: ホワイトリスト
     */
    enabledPrompts: z.array(z.string()).nullable().default([]),
    enabledSkills: z.array(z.string()).nullable().default([]),
    enabledExternalAgents: z.array(z.string()).nullable().default([]),
    enabledSecrets: z.array(z.string()).nullable().default([]),
    /** null: 全て有効（フィルタなし）、[]: 全て無効、string[]: ホワイトリスト */
    enabledVolumeMounts: z.array(z.string()).nullable().default([]),
    containerSandboxEnabled: z.boolean().optional().default(false),
    toolResultTruncateSize: z.number().nullable().optional().default(null),
    maxSteps: z.number().nullable().optional().default(null),
    preferredModel: ModelSelectionSchema.nullable().optional().default(null),
    /** 優先エフォート（推論レベル）。ワークスペースへ切り替えたときチャットのエフォートへ反映する */
    preferredReasoningLevel: ReasoningLevelSchema.nullable().optional().default(null),
    compressionConfig: CompressionConfigSchema.nullable().optional().default(null),
    templateVars: z.record(z.string(), z.string()).nullable().optional().default(null),
    hooks: WorkspaceHooksSchema.nullable().optional().default(null),
    hookFailSafeMode: HookFailSafeModeSchema.optional().default(null),
    samplePrompts: z.array(SamplePromptSchema).nullable().optional().default(null),
    initialChatMessage: InitialChatMessageSchema.optional().default(null),
    /**
     * このワークスペースのエージェントが名乗る名前。未設定（null）のときは
     * システムプロンプト生成が既定名 `DEFAULT_AGENT_NAME` を用いる（ワークスペース名へは
     * フォールバックしない）。応答の文体・分量は専用フィールドを設けず `rule` で制御する。
     */
    agentName: AgentNameSchema.optional().default(null),
    advisorConfig: AdvisorConfigSchema.optional().default(null),
    memoryConfig: MemoryConfigSchema.optional().default(null),
    approvalBypassConfig: ApprovalBypassConfigSchema.optional().default(null),
    aiApprovalConfig: AiApprovalConfigSchema.optional().default(null),
    webSearchConfig: WebSearchConfigSchema.optional().default(null),
    emailSendingConfig: EmailSendingConfigSchema.optional().default(null),
    completionCriteriaConfig: CompletionCriteriaConfigSchema.optional().default(null),
    sessionCostLimitConfig: SessionCostLimitConfigSchema.optional().default(null),
    documents: z.array(WorkspaceDocumentSchema).optional().default([]),
    /** 自動実行設定（スケジュール定義群・WebHook 定義群）。未設定は null（自動実行なし）。 */
    automationConfig: AutomationConfigSchema.optional().default(null),
    /**
     * セットアップ管理系ビルトインツール（専用ワークスペース作成・MCP登録・ボリュームマウント管理等）の提供可否。
     * 既定は無効（opt-in）。未保存の既存ワークスペースは false として解釈される（後方互換）。
     */
    enableSetupManagementTools: z.boolean().optional().default(false),
    /**
     * ワークスペース間タスク委譲の許可設定。未設定は null（委譲しない・受け入れない）。
     */
    peerMessagingConfig: WorkspacePeerMessagingConfigSchema.optional().default(null),
    /**
     * @deprecated 旧名。旧バージョンのクライアントが読み取るためにサーバー応答へ併記される
     * （値は enableSetupManagementTools と同一）。新規コードは参照しないこと。
     */
    enableWorkspaceManagementTools: z.boolean().optional(),
    /**
     * アプリ組み込みのシステムワークスペースであることを示す応答専用フラグ。
     * 合成応答のみが true を持ち、DB レコード（未保存の既存データ含む）は false と解釈される。
     * 作成・更新パラメータには存在しないため、ユーザー・エージェント・プラグインは設定できない。
     */
    isSystem: z.boolean().optional().default(false),
    isFavorite: z.boolean(),
    favoritedAt: z.number().nullable(),
    isDefault: z.boolean(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
/**
 * ワークスペース削除タスクアクション
 */
export const WorkspaceDeleteTaskActionSchema = z.enum(['move', 'delete']);
// ========================================
// パラメータ（共通フィールドを DRY 化）
// ========================================
/**
 * ワークスペース設定フィールドの単一の真実源（作成/更新パラメータの共通部分）。
 *
 * `export` しているのは型としての再利用のためだけではなく、`Object.keys(shape)` で
 * フィールド名一覧を実行時に取得し、クラウド保存・プラグイン公開/インストール・
 * 承認ダイアログ表示など「フィールドを個別列挙する手書き箇所」に対する
 * 網羅性テスト（parity test）を書けるようにするため。新フィールド追加時にこれらの
 * 手書き箇所を更新し忘れると、対応する parity テストが落ちる設計にしてある
 * （`docs/dev/workspace-settings-wiring.md` 参照）。
 */
export const WorkspaceSettingsFieldsSchema = z.object({
    description: z.string().optional(),
    rule: z.string().optional(),
    enabledTools: z.array(z.string()).nullable().optional(),
    enabledPrompts: z.array(z.string()).nullable().optional(),
    enabledSkills: z.array(z.string()).nullable().optional(),
    enabledExternalAgents: z.array(z.string()).nullable().optional(),
    enabledSecrets: z.array(z.string()).nullable().optional(),
    enabledVolumeMounts: z.array(z.string()).nullable().optional(),
    containerSandboxEnabled: z.boolean().optional(),
    toolResultTruncateSize: z.number().nullable().optional(),
    maxSteps: z.number().nullable().optional(),
    preferredModel: ModelSelectionSchema.nullable().optional(),
    preferredReasoningLevel: ReasoningLevelSchema.nullable().optional(),
    compressionConfig: CompressionConfigSchema.nullable().optional(),
    templateVars: z.record(z.string(), z.string()).nullable().optional(),
    hooks: WorkspaceHooksSchema.nullable().optional(),
    hookFailSafeMode: HookFailSafeModeSchema.optional(),
    samplePrompts: z.array(SamplePromptSchema).nullable().optional(),
    initialChatMessage: InitialChatMessageSchema.optional(),
    /** エージェントが名乗る名前（未設定は既定名。ワークスペース名へはフォールバックしない） */
    agentName: AgentNameSchema.optional(),
    advisorConfig: AdvisorConfigSchema.optional(),
    memoryConfig: MemoryConfigSchema.optional(),
    approvalBypassConfig: ApprovalBypassConfigSchema.optional(),
    aiApprovalConfig: AiApprovalConfigSchema.optional(),
    webSearchConfig: WebSearchConfigSchema.optional(),
    emailSendingConfig: EmailSendingConfigSchema.optional(),
    completionCriteriaConfig: CompletionCriteriaConfigSchema.optional(),
    sessionCostLimitConfig: SessionCostLimitConfigSchema.optional(),
    documents: z.array(WorkspaceDocumentSchema).optional(),
    automationConfig: AutomationConfigSchema.optional(),
    enableSetupManagementTools: z.boolean().optional(),
    /** ワークスペース間タスク委譲の許可設定 */
    peerMessagingConfig: WorkspacePeerMessagingConfigSchema.optional(),
    /**
     * @deprecated 旧名。旧バージョンのクライアントからの作成・更新リクエストを受け付けるための互換キー
     * （enableSetupManagementTools 未指定時のみサービス層でフォールバックされる）。新規コードは使用しないこと。
     */
    enableWorkspaceManagementTools: z.boolean().optional(),
});
/**
 * ワークスペース作成パラメータ
 */
export const WorkspaceCreateParamsSchema = WorkspaceSettingsFieldsSchema.extend({
    name: z.string().min(1, 'name is required'),
});
/**
 * ワークスペース更新パラメータ
 */
export const WorkspaceUpdateParamsSchema = WorkspaceSettingsFieldsSchema.extend({
    name: z.string().optional(),
    /**
     * 楽観的並行制御（CAS）の基準値。指定時、対象ワークスペースの現在の `updatedAt` と
     * 一致する場合のみ更新が適用され、不一致ならバックエンドが 409 Conflict を返す。
     */
    expectedUpdatedAt: z.number().optional(),
});
// ========================================
// リクエスト
// ========================================
/**
 * POST /workspaces リクエスト
 */
export const CreateWorkspaceRequestSchema = z.object({
    workspace: WorkspaceCreateParamsSchema,
});
/**
 * PUT /workspaces/{id} リクエスト
 */
export const UpdateWorkspaceRequestSchema = z.object({
    workspace: WorkspaceUpdateParamsSchema,
});
// ========================================
// レスポンス
// ========================================
/**
 * GET /workspaces レスポンス
 */
export const WorkspaceListResponseSchema = z.object({
    workspaces: z.array(WorkspaceSchema),
});
/**
 * GET /workspaces/{id} レスポンス
 */
export const WorkspaceDetailResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * POST /workspaces レスポンス
 */
export const CreateWorkspaceResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PUT /workspaces/{id} レスポンス
 */
export const UpdateWorkspaceResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PATCH /workspaces/{id}/favorite レスポンス
 */
export const ToggleFavoriteResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PATCH /workspaces/{id}/default レスポンス
 */
export const SetAsDefaultResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
// ========================================
// クエリ
// ========================================
/**
 * DELETE /workspaces/{id} クエリパラメータ
 */
export const DeleteWorkspaceParamsSchema = z.object({
    taskAction: WorkspaceDeleteTaskActionSchema.optional(),
    targetWorkspaceId: z.string().optional(),
});
//# sourceMappingURL=workspace-schemas.js.map